/**
 * EventModerationPanel - Super Admin panel for managing events
 * Allows blocking, unblocking, and closing events
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { eventAPI, moderationAPI, getErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  Calendar, Users, Search, MoreVertical, Eye, Ban, Lock, Unlock,
  AlertTriangle, CheckCircle2, XCircle, Loader2, RefreshCw,
  ExternalLink, ShieldAlert, ShieldCheck
} from 'lucide-react';

const EventModerationPanel = () => {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showUnblockDialog, setShowUnblockDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  // Fetch all events (super admin sees all)
  const { data: events, isLoading, refetch } = useQuery({
    queryKey: ['all-events'],
    queryFn: () => eventAPI.list(false).then(r => r.data), // Include inactive
  });

  // Block mutation
  const blockMutation = useMutation({
    mutationFn: ({ eventId, reason }) => moderationAPI.blockEvent(eventId, reason),
    onSuccess: () => {
      toast.success('Event has been blocked');
      queryClient.invalidateQueries(['all-events']);
      setShowBlockDialog(false);
      setSelectedEvent(null);
      setBlockReason('');
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, 'Failed to block event'));
    }
  });

  // Unblock mutation
  const unblockMutation = useMutation({
    mutationFn: (eventId) => moderationAPI.unblockEvent(eventId),
    onSuccess: () => {
      toast.success('Event has been unblocked');
      queryClient.invalidateQueries(['all-events']);
      setShowUnblockDialog(false);
      setSelectedEvent(null);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, 'Failed to unblock event'));
    }
  });

  // Close mutation
  const closeMutation = useMutation({
    mutationFn: (eventId) => moderationAPI.closeEvent(eventId),
    onSuccess: () => {
      toast.success('Event has been closed');
      queryClient.invalidateQueries(['all-events']);
      setShowCloseDialog(false);
      setSelectedEvent(null);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, 'Failed to close event'));
    }
  });

  // Filter events
  const filteredEvents = events?.filter(event => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      event.name?.toLowerCase().includes(query) ||
      event.slug?.toLowerCase().includes(query) ||
      event.description?.toLowerCase().includes(query)
    );
  });

  // Get status info
  const getStatusInfo = (event) => {
    if (event.status === 'blocked') {
      return { badge: <Badge className="bg-red-500/20 text-red-600"><Ban className="h-3 w-3 mr-1" /> Blocked</Badge>, color: 'red' };
    }
    if (event.status === 'closed') {
      return { badge: <Badge className="bg-slate-500/20 text-slate-600"><Lock className="h-3 w-3 mr-1" /> Closed</Badge>, color: 'slate' };
    }
    if (!event.is_active) {
      return { badge: <Badge className="bg-amber-500/20 text-amber-600"><AlertTriangle className="h-3 w-3 mr-1" /> Inactive</Badge>, color: 'amber' };
    }
    return { badge: <Badge className="bg-emerald-500/20 text-emerald-600"><CheckCircle2 className="h-3 w-3 mr-1" /> Active</Badge>, color: 'emerald' };
  };

  // Calculate stats
  const stats = {
    total: events?.length || 0,
    active: events?.filter(e => e.is_active && e.status !== 'blocked' && e.status !== 'closed').length || 0,
    blocked: events?.filter(e => e.status === 'blocked').length || 0,
    closed: events?.filter(e => e.status === 'closed').length || 0,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-indigo-600" />
            Event Moderation
          </h2>
          <p className="text-slate-500">Monitor and moderate events on the platform</p>
        </div>
        <Button variant="outline" onClick={() => refetch()} size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total Events</p>
            <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-emerald-600">Active</p>
            <p className="text-2xl font-bold text-emerald-600">{stats.active}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-red-600">Blocked</p>
            <p className="text-2xl font-bold text-red-600">{stats.blocked}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Closed</p>
            <p className="text-2xl font-bold text-slate-500">{stats.closed}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search events..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          data-testid="search-events"
        />
      </div>

      {/* Events List */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2 mb-4" />
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredEvents?.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No events found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredEvents?.map((event) => {
            const statusInfo = getStatusInfo(event);
            const totalSold = event.ticket_tiers?.reduce((acc, t) => acc + (t.sold || 0), 0) || 0;
            
            return (
              <Card 
                key={event.id} 
                className={`relative overflow-hidden transition-shadow hover:shadow-md ${
                  event.status === 'blocked' ? 'border-red-200 bg-red-50/30' : ''
                }`}
                data-testid={`event-card-${event.id}`}
              >
                {/* Color bar */}
                <div 
                  className="h-1" 
                  style={{ backgroundColor: event.branding?.primary_color || '#3B82F6' }}
                />
                
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">{event.name}</CardTitle>
                      <CardDescription className="truncate">
                        /{event.slug}
                      </CardDescription>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="flex-shrink-0" data-testid={`event-menu-${event.id}`}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => window.open(`/event/${event.slug}`, '_blank')}>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          View Public Page
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => window.open(`/admin/event/${event.id}`, '_blank')}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Admin Dashboard
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {event.status === 'blocked' ? (
                          <DropdownMenuItem 
                            onClick={() => {
                              setSelectedEvent(event);
                              setShowUnblockDialog(true);
                            }}
                            className="text-emerald-600"
                          >
                            <Unlock className="h-4 w-4 mr-2" />
                            Unblock Event
                          </DropdownMenuItem>
                        ) : event.status !== 'closed' && (
                          <DropdownMenuItem 
                            onClick={() => {
                              setSelectedEvent(event);
                              setShowBlockDialog(true);
                            }}
                            className="text-red-600"
                          >
                            <Ban className="h-4 w-4 mr-2" />
                            Block Event
                          </DropdownMenuItem>
                        )}
                        {event.status !== 'closed' && (
                          <DropdownMenuItem 
                            onClick={() => {
                              setSelectedEvent(event);
                              setShowCloseDialog(true);
                            }}
                            className="text-slate-600"
                          >
                            <Lock className="h-4 w-4 mr-2" />
                            Close Event
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="space-y-3">
                    {/* Status */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Status</span>
                      {statusInfo.badge}
                    </div>
                    
                    {/* Dates */}
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}
                      </span>
                    </div>
                    
                    {/* Registrations */}
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Users className="h-4 w-4" />
                      <span>{totalSold} registrations</span>
                    </div>
                    
                    {/* Block reason */}
                    {event.status === 'blocked' && event.block_reason && (
                      <div className="mt-2 p-2 bg-red-100 rounded text-xs text-red-700">
                        <strong>Block reason:</strong> {event.block_reason}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Block Event Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="h-5 w-5" /> Block Event
            </DialogTitle>
            <DialogDescription>
              Blocking an event will make it inaccessible to the public and its admins.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert className="bg-red-50 border-red-200">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-sm text-red-800">
                <strong>Warning:</strong> This action will:
                <ul className="list-disc ml-4 mt-1 space-y-1">
                  <li>Hide the event from public listings</li>
                  <li>Prevent new registrations</li>
                  <li>Block admin access to the event dashboard</li>
                </ul>
              </AlertDescription>
            </Alert>
            
            <div>
              <Label>Event: <span className="font-semibold">{selectedEvent?.name}</span></Label>
            </div>
            
            <div>
              <Label>Block Reason *</Label>
              <Textarea
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Provide a reason for blocking this event (e.g., Terms violation, Spam, Fraudulent activity...)"
                className="min-h-[100px]"
                data-testid="block-reason"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlockDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => blockMutation.mutate({ eventId: selectedEvent?.id, reason: blockReason })}
              disabled={blockMutation.isPending || !blockReason}
              data-testid="confirm-block"
            >
              {blockMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Blocking...
                </>
              ) : (
                <>
                  <Ban className="h-4 w-4 mr-2" />
                  Block Event
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unblock Event Dialog */}
      <Dialog open={showUnblockDialog} onOpenChange={setShowUnblockDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600">
              <Unlock className="h-5 w-5" /> Unblock Event
            </DialogTitle>
            <DialogDescription>
              This will restore access to the event for admins and the public.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert className="bg-emerald-50 border-emerald-200">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              <AlertDescription className="text-sm text-emerald-800">
                Unblocking will:
                <ul className="list-disc ml-4 mt-1 space-y-1">
                  <li>Restore the event to public listings</li>
                  <li>Allow new registrations</li>
                  <li>Restore admin access</li>
                </ul>
              </AlertDescription>
            </Alert>
            
            <div>
              <Label>Event: <span className="font-semibold">{selectedEvent?.name}</span></Label>
            </div>
            
            {selectedEvent?.block_reason && (
              <div className="p-3 bg-slate-100 rounded text-sm">
                <strong>Original block reason:</strong> {selectedEvent.block_reason}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnblockDialog(false)}>
              Cancel
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => unblockMutation.mutate(selectedEvent?.id)}
              disabled={unblockMutation.isPending}
              data-testid="confirm-unblock"
            >
              {unblockMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Unblocking...
                </>
              ) : (
                <>
                  <Unlock className="h-4 w-4 mr-2" />
                  Unblock Event
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Event Dialog */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-600">
              <Lock className="h-5 w-5" /> Close Event
            </DialogTitle>
            <DialogDescription>
              Closing an event marks it as completed/ended. This action is typically used after an event has concluded.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-sm text-amber-800">
                <strong>Note:</strong> Closing an event is different from blocking:
                <ul className="list-disc ml-4 mt-1 space-y-1">
                  <li>Admins can still view historical data</li>
                  <li>No new registrations will be accepted</li>
                  <li>Public page will show &quot;Event Ended&quot;</li>
                </ul>
              </AlertDescription>
            </Alert>
            
            <div>
              <Label>Event: <span className="font-semibold">{selectedEvent?.name}</span></Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={() => closeMutation.mutate(selectedEvent?.id)}
              disabled={closeMutation.isPending}
              data-testid="confirm-close"
            >
              {closeMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Closing...
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  Close Event
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventModerationPanel;
